import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, BarcodeAPI, BarcodeData, ButtonAPI, DeckAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, MapperAPI, PanelAPI, TextAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnChange, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, ClickEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, HttpStatus, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, CredentialsValidationSuccess, foundationContext, ParameterValue } from '@philips-internal/oswald-ui-foundation';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import { FragmentNames, InfusionAdministrationByScheduleAliases } from '../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { ActionType, AdminType, ChangeType, InfusionHourStatus, InfusionScreenType, OptionCheck } from '../../../../constants/infusion-solutions';
import { BarcodeUtils } from '../../../../utils/barcode-utils';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { InfusionSolutionsUtils } from '../../../../utils/infusion-solutions-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';
import BaseController from '../../../BaseController';
import { InfusionsViewerParams } from '../../infusion-viewer/infusions-viewer-params';
import { AdminModalsFractionalDoseService } from '../../services/admin-modals-fractional-dose-service';
import InfusionService, { CreateFirstSolutionCheckRequest } from '../../services/infusion-service';
import MedicationService, { ConsistAdministrationMarginInfoResponse, CreateEventMedicationConferenceRequest } from '../../services/medication-service';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../services/timeline-service';
import { AdminByScheduleParams } from '../administration-schedule/administration-schedule-params';
import { AdminByProcessParams } from '../medication-administration-by-process/medication-administration-by-process-params';
import { FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Conditionals, DisplayMessages } from '../administration-schedule/administration-schedule-types';
import { SolutionType } from '../../adjust-infusion-speed/adjust-infusion-speed-types';
import { YesNoType } from '../../../../utils/enum-utils';
import BarcodeService from '../../../../services/barcode-service';
import { ConsistAdministrationMarginInfo } from '../recommendation-orders/consist-administration-margin-info';

interface JWTToken {
  [key: string]: unknown;
  sub: string;
}

interface InfusionAdmnistrationScheduleForm {
  adminRoute: {
    administrationRouteCode: string;
    administrationRouteDescription: string;
  },
  currentTime: string;
  device: {
    unitMeasure: string;
    unitMeasureCode: number;
    unitMeasureType: string;
    unitMeasureValue: string;
  },
  dilution: string;
  infusionSpeed: string;
  infusionType: {
    unitMeasure: string,
    unitMeasureCode: number,
    unitMeasureType: string,
    unitMeasureValue: string
  },
  justification: string;
  observation: string;
  pumpType: Record<string, Value>,
  unityMeasure: {
    measurementUnitCode: string;
    measurementUnitDescription: string;
  },
  volumePhase: string;
}

interface ResponseBodyProps {
  hourDate?: string;
  informDose?: boolean;
  items?: Record<string, Value>[];
  materialCode?: number;
  message?: string;
  messageType?: string;
  read?: boolean;
  success?: boolean;
  total?: number;
  totalRead?: number;
  unitMeasure?: string;
  unitMeasureValue?: string;
}

@Controller({
  component: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_LAYOUT,
  fragment: FragmentNames.INFUSION_ADMINISTRATION_BY_SCHEDULE
})
class InfusionAdministrationByScheduleController extends BaseController {

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_NAME_TEXT)
  private infusionNameTextApi!: TextAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DATE_TIME_TEXT)
  private infusionDateTimeTextApi!: TextAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_NEW_DATE_INPUT)
  private infusionNewDateInputApi!: InputAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_AUTOCOMPLETE)
  private infusionDeviceAutocompleteApi!: AutocompleteAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_FETCH)
  private infusionDeviceFetchApi!: FetchAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_MAPPER)
  private infusionAdminRouteMapperApi!: MapperAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_OBSERVATION_FIELD)
  private infusionObservationFieldApi!: FieldAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_OBSERVATION_TEXT)
  private infusionObservationTextAreaApi!: TextAreaAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_JUSTIFICATION_TEXT)
  private readonly infusionJustificationTextAreaApi!: TextAreaAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BARCODE_PANEL)
  private infusionBarcodePanelApi!: PanelAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BARCODE)
  private infusionBarcodeApi!: BarcodeAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_CARDS_ACCORDION_PANEL)
  private infusionCardsAccordionPanelApi!: PanelAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_VOLUME_STEP_INPUT)
  private infusionVolumeStepInputApi!: InputAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_INFUSION_SPEED_INPUT)
  private infusionSpeedInputApi!: InputAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_CARD_DECK)
  private infusionCardDeckApi!: DeckAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BUTTON)
  private infusionButtonApi!: ButtonAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_INFUSION_FORM_FIELD)
  private infusionFormFieldApi!: FieldAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_FORM)
  private infusionFormApi!: FormAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DILUTION_FIELD)
  private infusionDilutionFieldApi!: FieldAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DILUTION_TEXT)
  private infusionDilutionTextApi!: TextAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_INFUSION_SPEED_FIELD)
  private infusionSpeedFieldApi!: FieldAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_VOLUME_STEP_FIELD)
  private infusionVolumeStepFieldApi!: FieldAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_INFUSION_FORM_AUTOCOMPLETE)
  private infusionFormAutocompleteApi!: AutocompleteAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_UNITY_AUTOCOMPLETE)
  private infusionUnityAutocompleteApi!: AutocompleteAPI;

  @Inject(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_ADMIN_ROUTE_AUTOCOMPLETE)
  private infusionAdminRouteAutocompleteApi!: AutocompleteAPI;

  validateIfBarcodeAndAccordionCardsIsVisible(): void {
    const tasyFunctionName = 'ADEP#9129';
    const param3 = FoundationUtils.findModuleParam(tasyFunctionName, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const isAdmProcess = AdminByProcessParams.admType.isAdmProcess;

    if (isAdmProcess || param3 === 'N') {
      this.infusionBarcodePanelApi.setVisible(false);
      this.infusionCardsAccordionPanelApi.setVisible(false);
      this.infusionButtonApi.setEnabled(true);
    } else if (InfusionsViewerParams.infusionScreenType === InfusionScreenType.RESTART_SOLUTION) {
      this.validateIfChangeVial();
    } else {
      this.resetAllInfusionCards().then(() => {
        this.setCardsAccordion();
      })
      this.infusionButtonApi.setEnabled(false);
    }
  }

  validateIfChangeVial(): void {
    InfusionSolutionsUtils.getInfusionCheckingState(ActionType.RESTART_SOLUTION).then(response => {
      if (response.changeVial === 'N') {
        this.infusionBarcodePanelApi.setVisible(false);
        this.infusionCardsAccordionPanelApi.setVisible(false);
        this.infusionButtonApi.setEnabled(true);
      } else {
        this.infusionButtonApi.setEnabled(false);
        this.resetAllInfusionCards().then(() => {
          this.setCardsAccordion();
        })
      }
    })
  }

  setCardsAccordion = (): Promise<Record<string, Value>[]> => {
    const { stepSolutionNumber, prescriptionId, solutionId } = InfusionsViewerParams.infusionDetailsParams;
    const params = {
      prescriptionNumber: prescriptionId,
      solutionSequenceNumber: solutionId,
      stepSolutionNumber: stepSolutionNumber
    }
    return HttpService.get(Endpoint.EMAR_FIND_INFUSION_BY_SCHEDULE, params)
      .then(res => {
        const infusions = res.body as Record<string, Value>[];
        const hasNoReading = infusions.filter(((item) => item.read === false)).length === 0;
        const hasReading = infusions.filter(((item) => item.read === true)).length > 0;

        this.infusionCardDeckApi?.setDone(true);
        this.infusionCardDeckApi?.setLoading(true);

        if (hasReading) {
          AdminByScheduleParams.readParam = { noCardsRead: false };
        }

        if (infusions?.length > 11) {
          this.infusionCardDeckApi?.setDone(false);
        }

        if (hasNoReading) {
          this.infusionButtonApi.setEnabled(true);
          this.infusionBarcodeApi.setEnabled(false);
          this.context.set({ allMedicationsRead: true });
        }

        if (infusions?.length > 0) {
          this.infusionCardDeckApi?.setData(infusions);
          this.infusionCardDeckApi?.setLoading(false);
        }

        return infusions;
      });
  }

  setInfusionButtonValue(): void {
    switch (InfusionsViewerParams.infusionScreenType) {
      case InfusionScreenType.START_SOLUTION:
        this.infusionButtonApi.setValue(MessageService.translate('button.infusion-viewer.start'));
        break;
      case InfusionScreenType.RESTART_SOLUTION:
        this.infusionButtonApi.setValue(MessageService.translate('button.infusion-viewer.restart-solution'));
        break;
      case InfusionScreenType.ADMIN_BY_SCHEDULE:
        this.infusionButtonApi.setValue(MessageService.translate('text.administration'));
        break;
      case InfusionScreenType.FIRST_CHECK:
        this.infusionButtonApi.setValue(MessageService.translate('tws.emar.perform-first-check'));
        break;
      default:
        break;
    }
  }

  getParam371(): ParameterValue | null {
    const adepFunctionName = 'ADEP#1113';
    return FoundationUtils.findModuleParam(adepFunctionName, ADEP_E1113Parameters.DISPLAY_DEVICES_INSTALLED_ON_PATIENT_WHEN_STARTING_THE_SOLUTION_ADM);
  }

  getInfusionBody(): Record<string, Value> {
    const timelineEvent = TimelineContext.get();
    const formRecord = this.infusionFormApi.getRecord() as unknown as InfusionAdmnistrationScheduleForm;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const currentTime = this.context.get('currentTime') as moment.Moment;
    const param3 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CHECK_MEDICATIONS_BEFORE_ADMIN);
    const param371 = this.getParam371();
    const infusionDetails = InfusionsViewerParams.infusionDetailsParams;
    const isAdmProcess = AdminByProcessParams.admType.isAdmProcess;
    const actionType = isAdmProcess ? AdminType.PROCESS : InfusionsViewerParams.actionType;
    const unitMeasureValue = formRecord.infusionType?.unitMeasureValue;
    const infusionType =
      unitMeasureValue !== AdminType.OPEN && unitMeasureValue !== AdminType.TIME &&
        unitMeasureValue !== AdminType.INFUSION_SPEED ? AdminType.INFUSION_SPEED : unitMeasureValue;
    const measureUnitCode = formRecord.unityMeasure?.measurementUnitCode;
    const dosageType =
      measureUnitCode !== AdminType.GRAMA &&
        measureUnitCode !== AdminType.MILIGRAMAS &&
        measureUnitCode !== AdminType.MILILITROS ? AdminType.MILILITROS_PER_HOUR : measureUnitCode;
    const formatedInfusionSpeed = formRecord.infusionSpeed.replace(',', '.');
    const infusionComponents = param3 === 'N' || !this.infusionCardsAccordionPanelApi.isVisible() ? [] : this.infusionCardDeckApi?.getData();

    const body = {
      actionType,
      changeDate: currentTime?.format(ISO_FULL_DATE_FORMAT),
      dateTime: timelineEvent.start,
      deviceSequence: param371 === 'N' ? null : formRecord.pumpType?.deviceCode,
      dosageType,
      encounterNumber: encounterId,
      infusionComponents: infusionComponents,
      infusionPump: param371 === 'N' ? formRecord.device?.unitMeasureValue : null,
      infusionSpeedQuantity: Number(formatedInfusionSpeed),
      infusionType,
      isPdoPrnStatus: timelineEvent.pdoPrn ? 'S' : 'N',
      itemCode: Number(timelineEvent.itemCode),
      justifyDescription: formRecord.justification ? String(formRecord.justification) : '',
      observationDescription: formRecord.observation,
      prescriptionNumber: Number(infusionDetails.prescriptionId),
      scheduleSequenceNumber: Number(timelineEvent.id),
      solutionSequence: infusionDetails.solutionId,
      statusSolution: timelineEvent.itemStatus,
      stepSolutionNumber: infusionDetails.stepSolutionNumber,
      volumePhaseQuantity: Number(String(formRecord.volumePhase).split(' ')[0])
    } as Record<string, Value>;

    return body;
  }

  readOneInfusionCard(barcodeRead: string): Promise<void | Record<string, Value>[]> {
    const infusionDetails = InfusionsViewerParams.infusionDetailsParams;

    const body = {
      barcode: barcodeRead,
      chemotherapyStatus: TimelineContext.get().chemotherapyStatus,
      doseQuantity: 0,
      encounterId: Number(this.historyService.getCurrentRouteParams().encounterId),
      groupingNumber: TimelineContext.get().groupingNumber,
      isChemoOrdenDisp: TimelineContext.get().isChemoOrdenDisp,
      isChemotherapy: TimelineContext.get().chemotherapy,
      items: this.infusionCardDeckApi?.getData(),
      listSeqMaterialInfusion: TimelineContext.get().listSeqMaterialInfusion,
      nrSeqOrdemChemotherapy: Number(TimelineContext.get().nrSeqOrdemChemotherapy),
      prescriptionNumber: infusionDetails.prescriptionId,
      scheduleSequence: Number(TimelineContext.get().id),
      solutionSequenceNumber: infusionDetails.solutionId,
      stepSolutionNumber: infusionDetails.stepSolutionNumber,
    }
    return HttpService.post(Endpoint.EMAR_READ_ONE_INFUSION_CARD, {}, { body }
    ).then((res) => {
      return res.body as Record<string, Value>[];
    });
  }

  resetAllInfusionCards(): Promise<void | Record<string, Value>[]> {
    const infusionDetails = InfusionsViewerParams.infusionDetailsParams;

    return HttpService.put(Endpoint.EMAR_RESET_ALL_INFUSION_CARDS, {
      prescriptionNumber: infusionDetails.prescriptionId,
      solutionSequenceNumber: infusionDetails.solutionId,
      stepSolutionNumber: infusionDetails.stepSolutionNumber
    }).then((res) => {
      return res.body as Record<string, Value>[];
    });
  }

  resetOneInfusionCard = (barcodeValue: string): void => {
    const infusionDetails = InfusionsViewerParams.infusionDetailsParams;

    const body = {
      items: this.infusionCardDeckApi?.getData(),
      prescriptionNumber: infusionDetails.prescriptionId,
      scheduleCurrentSequence: Number(barcodeValue),
      solutionSequenceNumber: infusionDetails.solutionId,
      stepSolutionNumber: infusionDetails.stepSolutionNumber,
    }

    HttpService.post(Endpoint.EMAR_RESET_ONE_INFUSION_CARD, {}, { body }).then((resp) => {
      const response = resp.body as Record<string, Value>[] as ResponseBodyProps;
      const responseItems = response.items ?? [];
      const totalRead = responseItems.filter((item) => item.read === true);
      const withoutReading = responseItems.filter((item) => item.read === false);

      MedicationService.showToast(String(response.message), ToastLevel.SUCCESS);

      if (withoutReading.length > 0) {
        this.infusionBarcodeApi.setEnabled(true);
        this.infusionButtonApi.setEnabled(false);
        this.context.set({ readPendding: false });
      }

      this.infusionCardDeckApi?.setData(responseItems);
      AdminByScheduleParams.readParam = { noCardsRead: true };

      if (totalRead.length > 0) {
        AdminByScheduleParams.readParam = { noCardsRead: false };
      }
    }).catch((error) => {
      const errorMessage = error.message;
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  handleBarcodeClose = (): void => {
    this.infusionBarcodeApi.close();
  }

  handleModalAdminScheduleFractionalDose(currentBarcode: string): void {
    const medicationViewerParams = InfusionsViewerParams.adminSchedule;

    const paramsAdmFractionDose = {
      barcodeClose: this.handleBarcodeClose,
      currentBarcode,
      medicationOrSolution: 'solution',
      prescriptionNumber: Number(medicationViewerParams?.prescriptionNumber),
      solutionSequenceNumber: Number(medicationViewerParams?.solutionSequenceNumber),
      stepSolutionNumber: Number(medicationViewerParams?.stepSolutionNumber),
      updatedCards: this.setCardsAccordion
    }

    AdminModalsFractionalDoseService.modalAdminScheduleFractionalDose(paramsAdmFractionDose);
  }

  toastType(message: string): ToastLevel {
    switch (message) {
      case 'SUCCESS':
        return ToastLevel.SUCCESS;
      case 'ERROR':
        return ToastLevel.ERROR;
      default:
        return ToastLevel.SUCCESS;
    }
  }

  firstMedicationChecked(): Promise<number> {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;

    const request: CreateFirstSolutionCheckRequest = {
      actionType: ChangeType.START,
      infusionType: SolutionType.SOLUTION,
      itemSequence: timelineMedication.itemSequence,
      prescriptionNumber: timelineMedication.prescriptionNumber,
      scheduleSequence: timelineMedication.id,
      userName: ''
    }

    return this.createFirstMedicationCheck(request);
  }

  private async createFirstMedicationCheck(request: CreateFirstSolutionCheckRequest): Promise<number> {
    const statusError = 400;

    return await InfusionService.createFirstInfusionCheck(request)
      .then((res) => {
        return res;
      }).catch(response => {
        const body = response.body;
        const hasErrors = !!body?.errors;
        if (hasErrors) {
          body.errors.forEach((message: string) => MedicationService.showToast(message, ToastLevel.ERROR));
        }
        return statusError;
      });
  }

  createEventMedicationConference = (): Promise<void> => {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const formValues = this.infusionFormApi.getRecord();
    const justification = formValues.observation;
    this.infusionButtonApi.setEnabled(false);

    const request: CreateEventMedicationConferenceRequest = {
      changeStatus: ChangeType.CONFERENCE_SOLUTION,
      changeDate: moment().format(ISO_FULL_DATE_FORMAT),
      encounterNumber: Number(encounterId),
      glucoseProteinSequence: 0,
      internalProcessSequence: 0,
      itemCode: Number(timelineMedication.itemCode),
      itemSequence: Number(timelineMedication.itemSequence) || 1,
      itemType: timelineMedication.itemType,
      justifyDescription: justification ? String(justification) : '',
      pdoPrn: timelineMedication.pdoPrn ? Conditionals.POSITIVE : Conditionals.NEGATIVE,
      prescriptionNumner: timelineMedication.prescriptionNumber,
      scheduleSequence: Number(timelineMedication.id),
      userName: ''
    }

    return MedicationService.createEventMedicationConference(request).then();
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_FORM })
  hadleMountInfusionAdminByScheduleLayout(): void {
    const materialCode = InfusionsViewerParams.infusionDetailsParams.materialCode;
    AdminByScheduleParams.readParam = { noCardsRead: true };

    if (this.getParam371() === 'N') {
      this.context.set({ hasDevice: true });
    }

    this.context.set({ materialCode });

    const { currentSpeedInfusion } = TimelineContext.get();
    const solutionRate = InfusionsViewerParams.infusionDetailsParams?.solutionRate ?? 0;
    const speedInfusion = currentSpeedInfusion ?? solutionRate;
    const { volumeInStepQty } = InfusionsViewerParams.infusionDetailsParams;
    const labelText = MessageService.translate('text.infusion-speed');
    const labelVolumeStep = `${MessageService.translate('label.infusion-viewer.volume-step')} (${MessageService.translate('tws.measure-label')})`

    this.infusionSpeedFieldApi.setLabel(labelText);
    this.infusionVolumeStepFieldApi.setLabel(labelVolumeStep);
    this.infusionVolumeStepInputApi.setValue(volumeInStepQty);
    this.infusionSpeedInputApi.setValue(String(speedInfusion));
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_UNITY_FETCH })
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const domain = 93;
    const situation = 'A';
    fetchReason.setParameters({ domain, situation }, { skip: true });
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_NEW_DATE_INPUT })
  handleMountInfusionNewDateInput(): void {
    const timelineInfo = TimelineContext.get();
    const dateTime = moment(timelineInfo.start);
    const newDate = moment();

    this.infusionNewDateInputApi.setValue(newDate.utc().format(ISO_FULL_DATE_FORMAT));
    this.infusionNameTextApi.setValue(timelineInfo.description);
    this.infusionDateTimeTextApi.setValue(dateTime.utc().format(ISO_FULL_DATE_FORMAT));
    this.context.set({ currentTime: newDate });
    this.context.set({ dateTime: newDate });
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_INFUSION_FORM_AUTOCOMPLETE })
  handleMountInfusionFormAutocomplete(): void {
    const domain = 1883;
    const situation = 'A';

    HttpService.get(Endpoint.EMAR_FIND_INFUSIONS_FORM, { domain, situation })
      .then(res => {
        const currentRecord = this.infusionFormApi.getRecord();
        const responseBody = res.body as ResponseBodyProps[];

        this.infusionFormApi.setRecord({
          ...currentRecord,
          infusionType: {
            unitMeasure: responseBody[0].unitMeasure,
            unitMeasureValue: responseBody[0].unitMeasure
          }})
      });
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_UNITY_FIELD })
  handleMountInfusionUnityAutocomplete(): void {
    const domain = 93;
    const situation = 'A';

    HttpService.get(Endpoint.EMAR_FIND_INFUSIONS_FORM, { domain, situation })
      .then(res => {
        const currentRecord = this.infusionFormApi.getRecord();
        const responseBody = res.body as ResponseBodyProps[];

        const dosageTypeDescription = InfusionsViewerParams.adminSchedule?.dosageTypeDescription;
        const unitMeasureType = responseBody.filter(((item) => item.unitMeasure === dosageTypeDescription));

        if (unitMeasureType[0]) {
          this.infusionFormApi.setRecord({
            ...currentRecord,
            unityMeasure: {
              unitMeasure: dosageTypeDescription,
              unitMeasureValue: unitMeasureType[0].unitMeasureValue
            }})
        }
      });
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_AUTOCOMPLETE })
  handleMountInfusionDeviceAutocomplete(): void {
    if (this.getParam371() === 'S') {
      const deviceDescription = 'deviceDescription';

      this.infusionDeviceAutocompleteApi.setEntry(deviceDescription);
      this.infusionDeviceAutocompleteApi.setPrimaryKey(deviceDescription);
    }
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_FETCH })
  handleMountInfusionDeviceFetch(): void {
    if (this.getParam371() === 'S') {
      const { encounterId } = this.historyService.getCurrentRouteParams();
      this.context.set({ encounterCode: encounterId });
      this.infusionDeviceFetchApi.setEndpoint(Endpoint.EMAR_FIND_INFUSIONS_DEVICE);
    }
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_MAPPER})
  handleMountInfusionDeviceMapper(): void {
    if (this.getParam371() === 'S') {
      const deviceDescription = 'deviceDescription';
      this.infusionAdminRouteMapperApi.setMap({ value: deviceDescription });
    }
  }

  @OnAfterFetch({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DEVICE_FETCH })
  handleAfterFetchDevice(event: FetchEvent): void {
    const fetch = event.target as unknown as FetchAPI;
    fetch.setParameters({ loading: false }, { skip: true });
    fetch.clearPersistedState();
    if (this.getParam371() === 'N' && InfusionsViewerParams.infusionDetailsParams?.infusionPump) {
      const currentRecord = this.infusionFormApi.getRecord();
      const infusionPump =  InfusionsViewerParams.infusionDetailsParams?.infusionPump;
      const infusionPumpInfo = Array.isArray(event.data) ? event.data.find((item: any) => item.unitMeasureValue === infusionPump) : null;
      if (infusionPumpInfo) {
        this.infusionFormApi.setRecord({
          ...currentRecord,
          device: {
            unitMeasure: infusionPumpInfo.unitMeasure,
            unitMeasureValue: infusionPump
          }
        });
      }
    }
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_ADMIN_ROUTE_AUTOCOMPLETE })
  handleMountInfusionAdminRouteAutocomplete(): void {
    const { prescriptionNumber } = this.historyService.getCurrentRouteParams();
    const params = {
      prescriptionNumber,
      sequenceNumber: InfusionsViewerParams.adminSchedule.solutionSequenceNumber
    }
    HttpService.get(Endpoint.EMAR_FIND_INFUSION_ADMINISTRATION_ROUTE, params)
      .then(res => {
        const currentRecord = this.infusionFormApi.getRecord();
        if (res.body) {
          this.infusionFormApi.setRecord({
            ...currentRecord,
            adminRoute: res.body})
        }
      });
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_INFUSION_FORM_FIELD })
  handleMountInfusionObservationText(): void {
    const observationDescription = TimelineContext.get().observationDescription;
    const infusionScreenType = InfusionsViewerParams.infusionScreenType;

    if (infusionScreenType === InfusionScreenType.RESTART_SOLUTION) {
      this.infusionObservationFieldApi.setVisible(false);
      this.infusionFormFieldApi.setVisible(false);
      return;
    }

    if (observationDescription) {
      this.infusionObservationTextAreaApi.setValue(observationDescription);
    }
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_DILUTION_TEXT })
  handleMountInfusionDilutionText(): void {
    const { dilutionDescription } = InfusionsViewerParams.infusionDetailsParams;

    if (!dilutionDescription) {
      this.infusionDilutionFieldApi.setVisible(false);
      return;
    }

    this.infusionDilutionTextApi.setValue(dilutionDescription);
  }

  @OnChange({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_NEW_DATE_INPUT })
  handleChangeInfusionNewDateInput(evt: ChangeEvent<InputAPI>): void {
    this.context.set({ currentTime: moment(String(evt.value)) });
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_LAYOUT })
  async handleMountInfusionLayout(): Promise<void> {
    const { hourStatus } = this.historyService.getCurrentRouteParams();
    const notIsFirstInfusionChecked = hourStatus !== InfusionHourStatus.CHECKED_FIRST;
    if (notIsFirstInfusionChecked) {
      return
    }

    const shouldPerformSecondCheck = this.shouldPerformSecondCheck();
    if (shouldPerformSecondCheck) {
      await this.handleSecondMedicationCheck();
    }
  }

  private shouldPerformSecondCheck(hasFirstCheck: boolean = false): boolean {
    const confirmSecondCheck = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const { hourStatus } = this.historyService.getCurrentRouteParams();
    const doubleCheckPerformed = this.context.get('doubleCheckPerformed');

    return confirmSecondCheck === YesNoType.YES && !doubleCheckPerformed && (hasFirstCheck || hourStatus === InfusionHourStatus.CHECKED_FIRST);
  }

  private async handleSecondMedicationCheck(): Promise<void> {
    const userSecondCheckValid = await InfusionService.getInfoCheckMedication(OptionCheck.SAME_USER);
    this.disableAllFields();

    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    if (userSecondCheckValid && param5 === YesNoType.NO) {
      this.invalidUserToSecondCheck(userSecondCheckValid, true);
    }
  }

  private disableAllFields(): void {
    this.infusionNewDateInputApi.setEnabled(false);
    this.infusionFormAutocompleteApi.setEnabled(false);
    this.infusionDeviceAutocompleteApi.setEnabled(false);
    this.infusionDeviceAutocompleteApi.setEnabled(false);
    this.infusionUnityAutocompleteApi.setEnabled(false);
    this.infusionAdminRouteAutocompleteApi.setEnabled(false);
    this.infusionVolumeStepInputApi.setEnabled(false);
    this.infusionSpeedInputApi.setEnabled(false);
    this.infusionObservationTextAreaApi.setEnabled(false);
    this.infusionJustificationTextAreaApi.setEnabled(false);
  }

  private invalidUserToSecondCheck(message: string, disableFields: boolean): void {
    MedicationService.showToast(message, ToastLevel.ERROR);
    disableFields && this.infusionButtonApi.setEnabled(!disableFields);
  }

  @OnMount({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BUTTON })
  async handleMountInfusionButton():  Promise<void> {
    this.setInfusionButtonValue();
    const msgInvalid = await InfusionService.getInfoCheckMedication(OptionCheck.SAME_USER, '');
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    if(msgInvalid && param5 === YesNoType.NO) {
      applicationContext.popNavigationItem();
      MedicationService.showToast(msgInvalid, ToastLevel.ERROR);
    } else {
      this.validateIfBarcodeAndAccordionCardsIsVisible();
    }
  }

  @OnClick({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BUTTON })
  async handleScheduleButtonPress(): Promise<void> {
    const { hourStatus } = this.historyService.getCurrentRouteParams();
    const isFirstCheck = await this.isFirstCheck(String(hourStatus));
    const isValid = await this.infusionFormApi.validate();
    this.infusionButtonApi.setEnabled(false);

    if (!isValid) {
      this.infusionButtonApi.setEnabled(true);
      return;
    }

    const body = this.getInfusionBody();

    if (isFirstCheck && !this.context.get('firstCheckCreated')) {
      await this.handleFirstCheckCreation(body);
    } else {
      this.validateInfusionProcessDoubleCheck(body);
      this.infusionButtonApi.setEnabled(true);
    }
  }

  private async isFirstCheck(hourStatus: string): Promise<boolean> {
    const isValidStatus = hourStatus == InfusionHourStatus.PENDING || hourStatus == InfusionHourStatus.PREPARATION;
    const option = isValidStatus ? OptionCheck.FIRST_CHECK : OptionCheck.SAME_USER;
    return (isValidStatus &&
      InfusionsViewerParams.infusionScreenType === InfusionScreenType.FIRST_CHECK) ||
      await InfusionService.validateStatusMedication(option);
  }

  private async handleFirstCheckCreation(body: Record<string, Value>): Promise<void> {
    const firstCheckCreatedStatus = await this.firstMedicationChecked();

    if (firstCheckCreatedStatus >= HttpStatus.OK && firstCheckCreatedStatus < HttpStatus.MULTIPLE_CHOICES) {
      this.proccessAfterCreateFirstCheck(body);
    } else {
      this.infusionButtonApi.setEnabled(true);
      MedicationService.showToast(DisplayMessages.FIRST_CHECK_NOT_POSSIBLE, ToastLevel.WARNING);
    }
  }

  public proccessAfterCreateFirstCheck(bodyInfusion: Record<string, Value>): void {
    this.disableAllFields();
    this.infusionButtonApi.setValue('button.infusion-viewer.start');
    this.context.set({firstCheckCreated: true});
    MedicationService.showToast(DisplayMessages.FIRST_MED_CHECK_CREATED, ToastLevel.SUCCESS);
    this.validateInfusionProcessDoubleCheck(bodyInfusion);
  }

  public async validateInfusionProcessDoubleCheck(body: Record<string, Value>): Promise<void> {
    const promptConfig = {
      onDismiss: () => { this.infusionButtonApi.setEnabled(true); },
      onError: () => InfusionService.createOnCredentialValidationErrorCallback(),
      onSuccess: async (event: CredentialsValidationSuccess) => {
        const payloadSymbol = Object.getOwnPropertySymbols(event.token).find(s => s.description === 'payload');
        const userValidateCredentials = payloadSymbol && ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol]
          ? ((event.token as unknown) as { [key: symbol]: JWTToken })[payloadSymbol].sub : ' ';
        const userSecondCheckInvalid = await InfusionService.getInfoCheckMedication(OptionCheck.SAME_USER, userValidateCredentials);
        if (userSecondCheckInvalid) {
          this.invalidUserToSecondCheck(userSecondCheckInvalid, false);
          this.validateInfusionProcessDoubleCheck(body);
          return;
        }
        body.userName = userValidateCredentials;
        this.createEventMedicationConference()
          .then(() => {
            const changeDate = this.updateChangeDateIfItIsBeforeCurrentDate(body.changeDate as string)
            this.validateMarginAndStartInfusion({ ...body, changeDate });
          })
      },
      validateButtonExpression: MessageService.translate(DisplayMessages.VALIDATE_BUTTON_EXP),
    }

    AdminByScheduleParams.readParam = { noCardsRead: true };

    const firstCheckCreated = this.context.get('firstCheckCreated');
    const param5 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const confirmSecondCheck = this.shouldPerformSecondCheck(firstCheckCreated && param5 === YesNoType.YES);

    if (this.context.get('firstCheckCreated') && param5 === YesNoType.NO) {
      InfusionService.goToTimeline();
    } else {
      const { hourStatus } = this.historyService.getCurrentRouteParams();
      const ischeck = await this.isFirstCheck(String(hourStatus));
      const permittedStatus = (hourStatus == InfusionHourStatus.PENDING || hourStatus == InfusionHourStatus.PREPARATION);
      if (confirmSecondCheck && ischeck) {
        foundationContext.promptCredentialsValidation(promptConfig);
      } else if(confirmSecondCheck && hourStatus == InfusionHourStatus.CHECKED_FIRST && param5 === YesNoType.YES) {
        foundationContext.promptCredentialsValidation(promptConfig);
      }  else if(confirmSecondCheck && !ischeck && permittedStatus && param5 === YesNoType.YES) {
        foundationContext.promptCredentialsValidation(promptConfig);
      } else {
        this.validateMarginAndStartInfusion(body);
      }
    }
  }

  @OnMount(InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BARCODE)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_BARCODE })
  handleReadSuccess(event: ReadEvent<BarcodeData>): void {
    const { preventDefault } = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }

    AdminByScheduleParams.readParam = { noCardsRead: true };
    const eventValue = event.item.value.trim();

    this.readOneInfusionCard(eventValue).then((responseBody) => {
      const { totalRead, total, message, messageType, informDose, items } = responseBody as ResponseBodyProps;

      if (informDose) {
        this.handleModalAdminScheduleFractionalDose(eventValue);
        return;
      }

      if (!message) {
        MedicationService.showToast(MessageService.translate('text.medication-read-with-success'), ToastLevel.SUCCESS);
      } else {
        MedicationService.showToast(String(message), this.toastType(String(messageType)));
      }

      this.infusionButtonApi.setEnabled(false);
      this.infusionBarcodeApi.setEnabled(true);
      this.context.set({ allMedicationsRead: false });

      this.infusionCardDeckApi?.setData(items ?? []);

      if (Number(totalRead) > 0) {
        AdminByScheduleParams.readParam = { noCardsRead: false };
      }

      if (total !== 0 && totalRead === total) {
        this.infusionButtonApi.setEnabled(true);
        this.infusionBarcodeApi.setEnabled(false);
        this.infusionBarcodeApi.close();
        this.context.set({ allMedicationsRead: true });
        return;
      }
    }).catch((error) => {
      const errorMessage = error.body.errors[0];
      MedicationService.showToast(errorMessage, ToastLevel.ERROR);
    });
  }

  @OnClick({ target: InfusionAdministrationByScheduleAliases.INFUSION_ADMIN_BY_SCHEDULE_CARD })
  handleClickResetButton(event: ClickEvent): void {
    const scheduleSequence = event.value.scheduleSequence;

    BarcodeUtils.dialogModalConfirm(scheduleSequence, this.resetOneInfusionCard);
  }

  private async validateMarginAndStartInfusion(body: Record<string, Value>): Promise<void> {
    const infusionScreenType = InfusionsViewerParams.infusionScreenType;
    const param712 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.VALIDATE_SOLUTION_MARGIN_ALL_STEPS);
    const isRestartSolution = infusionScreenType === InfusionScreenType.RESTART_SOLUTION;
    const shouldValidateMargin = !isRestartSolution || param712 === 'S';

    if (!shouldValidateMargin) {
      InfusionService.startInfusion(body);
      return;
    }

    try {
      const response = await this.consistAdministrationMarginInfoResponse();
      if (response.responseDescription) {
        MedicationService.showToast(response.responseDescription, ToastLevel.WARNING);
        this.infusionButtonApi.setEnabled(true);
        return;
      }
      InfusionService.startInfusion(body);
    } catch (error: any) {
      this.infusionButtonApi.setEnabled(true);
      if (error?.body?.errors) {
        error.body.errors.forEach((message: string) => {
          MedicationService.showToast(message, ToastLevel.ERROR);
        });
      } else if (error?.body?.message) {
        MedicationService.showToast(error.body.message, ToastLevel.ERROR);
      }
    }
  }

  /*
  * This method is necessary because the @startInfusion function
  * aways should execute with the date after the @createEventMedicationConference function
  */
  private updateChangeDateIfItIsBeforeCurrentDate(eventDate: string): string {
    const currentDate = moment().add(50, 'seconds').format(ISO_FULL_DATE_FORMAT)
    return moment(eventDate).isBefore(currentDate) ? currentDate : eventDate
  }

  private consistAdministrationMarginInfoResponse(): Promise<ConsistAdministrationMarginInfoResponse> {
    const formRecord = this.infusionFormApi.getRecord() as unknown as InfusionAdmnistrationScheduleForm;
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();
    const params = {
      administrationDate: formRecord.currentTime,
      itemType: PrescriptionType.SOLUTION,
      justification: formRecord.justification,
      scheduleSequenceNumber: scheduleSequenceNumber as number
    };

    return ConsistAdministrationMarginInfo.execute(params);
  }
}

export default InfusionAdministrationByScheduleController;
